import type { ObjectId } from '@freightview/object-id'

import type { AccountBillTo } from '../models/account-bill-to.ts'
import { accountSamlStatuses } from '../models/account-security.ts'
import type { AccountSettings } from '../models/account-settings.ts'
import type { AccountBranding } from '../models/branding.ts'
import type { AccountRoleType, ProductType } from '../models/common.ts'
import type { Country, CountryUpper } from '../models/country.ts'
import {
  vArray,
  vBoolean,
  vDate,
  vEmail,
  vNonEmptyString,
  vObject,
  vOptional,
  type vOutput,
  vPicklist,
  vString,
} from '../validation/index.ts'
import type { InternalAccountSettings } from './account-settings.ts'
import type { AccountUser } from './account-user.ts'

type ACH_Status =
  | 'new'
  | 'pending'
  | 'verified'
  | 'verification_failed'
  | 'subscribed'

export type AccountLocation = {
  _id: ObjectId
  formatted: string
  street: string
  city: string
  state: string
  stateCode: string
  country: CountryUpper
  postalCode: string
  lat: number
  lng: number
  timezone: string
}

export type AccountLocationUpsertModel = Pick<
  AccountLocation,
  'street' | 'city' | 'state' | 'postalCode'
> & {
  country: Country
}

export const accountHistory = vObject({
  summary: vNonEmptyString(),
  userDisplayName: vOptional(vString()),
  userId: vString(),
  adminUserId: vOptional(vString()),
  createdDate: vDate(),
})

export type AccountHistory = vOutput<typeof accountHistory>

export const accountAdminShipmentExportHistory = vObject({
  adminUserId: vString(),
  userDisplayName: vString(),
  createdDate: vDate(),
  pickupDate: vString(),
  modes: vArray(vString()),
  statuses: vArray(vString()),
  additionalAccounts: vArray(vString()),
  emails: vArray(vString()),
})

export type AccountAdminShipmentExportHistory = vOutput<
  typeof accountAdminShipmentExportHistory
>

export type AccountNoteDto = {
  organization_id: string
  user_id: string
  user_name: string
  updated_by: string
  updated_by_name: string
  date_updated: string
  created_by: string
  created_by_name: string
  note_html: string
  note: string
  contact_id: string
  date_created: string
  id: string
  lead_id: string
}

// Carrier overrides is used to maintain a consistent carrierId from the point of view of the external API
// Though not recommended, some accounts may be using our internal carrierId in their own system.
// To better manage our carriers, we need to be able to merge duplicate carriers into eachother
// and that can sometimes lead to problems in external systems if our carrier id changes.
export type AccountCarrierOverrides = {
  carrierId: ObjectId
  customId: string
}

// TODO - this is almost a complete copy of the Account model in core-api/business/models/account/account.ts
// Eventually we should combine the two and place it in models ... but it feels weird to put that in admin,
// but it also feels weird to put it in base models since on the client side, admin is the only place that will use it.
// Not the purpose of the current PR though, so for now it starts the process of getting it into models
export type Account = {
  _id: ObjectId
  created: Date
  registration: {
    company?: string
    billTo: AccountBillTo[]
  }
  apiKey: string
  campaign: string
  products: ProductType[]
  roles: AccountRoleType[]
  users: AccountUser[]
  verified: boolean
  verifiedAt?: Date
  verifiedBy?: ObjectId
  spotQuoteContacts?: {
    userId: ObjectId
    groups?: string[]
  }[]
  spotQuoteGroups?: {
    groupId: ObjectId
    groupName: string
  }[]
  settings?: AccountSettings
  internalSettings?: InternalAccountSettings
  recent?: {
    emergencyName?: string
    emergencyPhone?: string
    emergencyPhoneExt?: string
    emergencyContractNumber?: string
  }

  subscription: {
    ach?: {
      authorized: {
        username: string
        signed: Date | null
      }
      created: Date
      source: string
      status: ACH_Status
      updated: Date
    }

    ach_push?: {
      account_number: string | null
      balance: number
      created: Date
      routing_number: string | null
      source: string | null
      status: 'new' | 'pending' | 'chargeable'
      updated: Date
    }

    customerId?: string
    ends?: Date
    source_type?: string | null
    status: string
    plan: string
    plan_id: string
    reserved_plan: string
  }
  locations: AccountLocation[]
  removedLocations: AccountLocation[]
  // tags?: AccountTag[]
  disabled?: boolean
  // carriers: AccountCarrier[]
  carrierOverrides?: AccountCarrierOverrides[]
  api?: {
    clientId: string
  }
  stats?: {
    referrals?: number
    registration?: {
      enrollmentFinishDate?: Date
    }
    commission: {
      firstSubPayment?: Date
      firstShipment?: Date
      thirdShipment?: Date
      tenthShipment?: Date
    }
  }

  features?: {
    flexBillTo?: boolean
  }

  crm?: {
    id?: string
  }
  brands?: AccountBranding[]
}
export const setPinnedToProdSchema = vObject({
  pinnedToProd: vBoolean(),
})
export type SetPinnedToProdDto = vOutput<typeof setPinnedToProdSchema>

export type AdminAccountModel = Omit<
  Account,
  'apiKey' | 'settings' | 'users'
> & {
  settings?: AccountSettings
  internalSettings?: InternalAccountSettings
  owner: AccountUser
  pinnedToProd?: boolean
}
export const accountSecuritySchema = vObject({
  mfa: vOptional(
    vObject({
      forceMfa: vBoolean(),
    }),
  ),
  saml: vOptional(
    vObject({
      status: vPicklist([...accountSamlStatuses, '']),
      idpMetadataUrl: vString(),
      idpEntityId: vString(),
      idpRedirectUrl: vString(),
      idpCert: vString(),
      jitProvisioning: vBoolean(),
      forceAuthn: vBoolean(),
      requiresSignedAuthn: vBoolean(),
      wantAssertionsSigned: vBoolean(),
      wantAuthnResponseSigned: vBoolean(),
      emailDomains: vString(),
      domainBlacklist: vString(),
      attributeMapping: vOptional(
        vObject({
          email: vOptional(vString()),
          firstName: vOptional(vString()),
          lastName: vOptional(vString()),
          jobTitle: vOptional(vString()),
          phoneNumber: vOptional(vString()),
          groups: vOptional(vString()),
        }),
      ),
    }),
  ),
})

export type AccountSecurityRequest = vOutput<typeof accountSecuritySchema>

export type AccountServiceProviderInfo = {
  entityId: string
  acsUrl: string
  certificate: string
}

export interface AccountChrConfig {
  cCode?: string
  sevenLetter?: string
  branchId?: string
  orderCreationEmail?: string
}

export const updateChrConfigSchema = vObject({
  cCode: vOptional(vString()),
  sevenLetter: vOptional(vString()),
  branchId: vOptional(vString()),
  orderCreationEmail: vOptional(vEmail),
  sendEmail: vOptional(vBoolean()),
})

export type UpdateChrConfigRequest = vOutput<typeof updateChrConfigSchema>

export const adminAccountSearchSchema = vObject({
  search: vString(),
})
export type AdminAccountSearchQuery = vOutput<typeof adminAccountSearchSchema>

export const toggleAccountSchema = vObject({
  disabled: vBoolean(),
})
export type ToggleAccountDto = vOutput<typeof toggleAccountSchema>

export const toggleFlexBillToSchema = vObject({
  enable: vBoolean(),
})
export type ToggleFlexBillToDto = vOutput<typeof toggleFlexBillToSchema>
